import { useEffect, type ReactNode } from "react";
import type { StepStatus } from "./advisoryApi";

// Small shared pieces for the Advisory area, built only from design.md idioms.

export function PercentBar({ value, compact = false }: { value: number | null | undefined; compact?: boolean }) {
  const v = typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
  return (
    <div className={compact ? "adv-percent compact" : "adv-percent"}>
      <div
        className="adv-percent-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={v ?? undefined}
      >
        <div className={v === 100 ? "adv-percent-fill done" : "adv-percent-fill"} style={{ width: `${v ?? 0}%` }} />
      </div>
      <span className="adv-percent-label">{v === null ? "—" : `${Math.round(v)} %`}</span>
    </div>
  );
}

/**
 * Check-row circle for a QP step status. Open steps stay neutral until a
 * submit attempt (QP only turns missing items red after the advisor tries).
 */
export function checkClass(status: StepStatus, attempted: boolean): string {
  if (status === "complete") return "check-row check-pass";
  if (status === "not_applicable") return "check-row adv-check-na";
  return attempted ? "check-row check-block" : "check-row adv-check-open";
}

export function checkGlyph(status: StepStatus, attempted: boolean): string {
  if (status === "complete") return "✓";
  if (status === "not_applicable") return "–";
  return attempted ? "!" : "";
}

export function PoweredBy() {
  return <p className="adv-powered">Powered by Quantfolio</p>;
}

export function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={wide ? "modal modal-wide" : "modal"} role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/** Segmented single choice (`.seg`), for compact answers inside a form. */
export function Seg<T extends string>({
  value,
  options,
  onChange,
  disabled,
  label,
}: {
  value: T | null | undefined;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <div className="seg adv-seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={value === o.value ? "seg-option active" : "seg-option"}
          onClick={() => onChange(o.value)}
          disabled={disabled}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Footer of a step form: the step's single primary Save, a quiet saved/dirty
 * note, and any error. `readOnly` hides the button (submitted advice).
 */
export function SaveBar({
  onSave,
  saving,
  dirty,
  error,
  readOnly,
  label = "Save",
  disabled,
  extra,
  note,
}: {
  onSave: () => void;
  saving: boolean;
  dirty: boolean;
  error: string | null;
  readOnly: boolean;
  label?: string;
  disabled?: boolean;
  extra?: ReactNode;
  /** Replaces the saved/unsaved status text. */
  note?: string;
}) {
  if (readOnly) return null;
  return (
    <div className="adv-savebar">
      {error && <div className="banner-error adv-savebar-error">{error}</div>}
      <span className="muted">{saving ? "Saving…" : (note ?? (dirty ? "Unsaved changes" : "All changes saved"))}</span>
      {extra}
      <button type="button" className="btn btn-primary adv-btn" onClick={onSave} disabled={saving || !dirty || disabled}>
        {saving ? "Saving…" : label}
      </button>
    </div>
  );
}

/** Calm note when Andersen data has been applied to a form but not yet saved. */
export function PrefillNote({ basis, onDismiss }: { basis: string[]; onDismiss: () => void }) {
  return (
    <div className="adv-prefill-note">
      <div>
        <strong>Andersen data applied — review it, then save.</strong>
        {basis.length > 0 && (
          <ul>
            {basis.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}
      </div>
      <button type="button" className="link-action" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  );
}

export function Unavailable({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <div className="empty adv-unavailable">
      <span className="eyebrow">Advice engine unavailable</span>
      <p>
        We can't reach the advice engine just now, so this advice can't be read or changed. Nothing
        has been lost — try again in a moment.
      </p>
      {message && <p className="muted">{message}</p>}
      {onRetry && (
        <button type="button" className="btn btn-ghost adv-btn" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * Where a proposed figure came from: Andersen's own records, or a demo
 * estimate the advisor still has to confirm with the client.
 */
export function SourceTag({ source }: { source: "andersen" | "estimate" }) {
  return source === "estimate" ? (
    <span className="adv-src-tag adv-src-estimate" title="A demo estimate — Andersen has no data for this. Confirm it with the client.">
      Estimate — confirm with the client
    </span>
  ) : (
    <span className="adv-src-tag adv-src-andersen" title="From the client's Andersen accounts">
      From Andersen
    </span>
  );
}

/** Amber note beside PrefillNote: which figures are estimates, and why. */
export function EstimateNote({ basis, saved }: { basis: string[]; saved: boolean }) {
  return (
    <div className="adv-estimate-note">
      <strong>
        {saved
          ? "Some figures are still estimates — confirm them with the client."
          : "Figures marked Estimate are a demo estimate — confirm each with the client."}
      </strong>
      {basis.length > 0 && !saved && (
        <ul>
          {basis.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      )}
      <span className="muted">Editing an estimated figure makes it yours and removes the tag.</span>
    </div>
  );
}
